import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Buildings, UsersThree, ChatCircleDots, CalendarCheck,
  Sparkle, Lightning, ChartBar, CurrencyDollar, DotsThreeVertical,
  Lock, Play, Pause, XCircle, CircleNotch, WhatsappLogo, Receipt,
  ClockCounterClockwise, SignIn, DownloadSimple, Robot,
  User as UserIcon, Gear, Gift, ArrowUUpLeft, Rocket, ArrowsClockwise,
  Trash, Warning, Coins,
} from "@phosphor-icons/react";
import { HealthDetailCard } from "@/components/admin/HealthScore";
import { startImpersonate } from "@/lib/sessionStore";

const TABS = [
  { id: "geral",      label: "Geral" },
  { id: "uso",        label: "Uso" },
  { id: "ia",         label: "IA" },
  { id: "whatsapp",   label: "WhatsApp" },
  { id: "financeiro", label: "Financeiro" },
  { id: "auditoria",  label: "Auditoria" },
];

function fmtBRLn(brl) {
  return Number(brl || 0).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 2,
  });
}

function fmtTokens(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} mi`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} k`;
  return v.toString();
}

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
}

function StatRow({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-b-0">
      <span className="text-[12px] text-slate-400">{label}</span>
      <span className={`text-sm tabular-nums ${accent || "text-white"}`}>{value}</span>
    </div>
  );
}

function Card({ title, children, icon: Icon, action }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} weight="regular" className="text-slate-500" />}
          <h3 className="font-heading text-[13px] font-semibold text-white tracking-tight">{title}</h3>
        </div>
        {action}
      </header>
      {children}
    </div>
  );
}

/* ─────────── consolidated financial snapshot ─────────── */
function FinancialSnapshotCard({ snap, tokensTotal, aiCalls, periodDays }) {
  const margin = snap.margin_brl ?? 0;
  const totalRevenue = snap.total_revenue_brl ?? snap.mrr_brl ?? 0;
  const isLoss = totalRevenue > 0 && margin < 0;
  const isNoRevenue = totalRevenue === 0;
  const totalCalls = Object.values(aiCalls || {}).reduce((a, b) => a + b, 0);
  const hasPacks = (snap.pack_revenue_brl || 0) > 0;

  return (
    <div
      data-testid="financial-snapshot"
      className={`relative rounded-lg p-6 border ${
        isLoss ? "bg-rose-500/[0.05] border-rose-500/20"
          : isNoRevenue ? "bg-white/[0.02] border-white/10"
          : "bg-emerald-500/[0.04] border-emerald-500/20"
      }`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <CurrencyDollar size={16} weight="regular" className={isLoss ? "text-rose-400" : "text-emerald-400"} />
          <h2 className="font-heading text-[15px] font-semibold text-white tracking-tight">Rentabilidade deste cliente</h2>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">últimos {periodDays}d</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <SnapBlock label="Receita total" value={fmtBRLn(totalRevenue)}
          hint={hasPacks
            ? `plano ${fmtBRLn(snap.mrr_brl)} + pacotes ${fmtBRLn(snap.pack_revenue_brl)}`
            : (isNoRevenue ? "cancelado / suspenso" : `MRR do plano ativo`)}
          accent={isNoRevenue ? "text-slate-300" : "text-emerald-400"} />
        <SnapBlock label="Custo bruto IA" value={fmtBRLn(snap.cost_brl)}
          hint={`${fmtTokens(tokensTotal)} tokens · ${totalCalls} chamadas`}
          accent={(snap.cost_brl || 0) > totalRevenue ? "text-rose-400" : "text-amber-400"} />
        <SnapBlock label="Margem" value={fmtBRLn(margin)}
          hint={snap.margin_pct != null ? `${snap.margin_pct}% da receita` : (isNoRevenue ? "sem receita no período" : "—")}
          accent={isLoss ? "text-rose-400" : margin > 0 ? "text-emerald-400" : "text-slate-300"} />
        <SnapBlock label="Receita gerada (cliente)" value={
          new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
            .format((snap.ai_revenue_cents || 0) / 100)
        }
          hint="atribuída à IA · ticket médio configurado"
          accent="text-emerald-400" />
      </div>

      {isLoss && (
        <div className="mt-5 p-3 bg-rose-500/10 border border-rose-500/30 rounded-md">
          <div className="text-[12.5px] text-rose-300 font-medium">⚠️ Cliente operando no prejuízo</div>
          <div className="text-[11.5px] text-rose-200/80 mt-0.5">
            O custo de IA superou a receita no período. Considere oferecer um pacote
            extra de créditos ou investigar o que está disparando tantas chamadas.
          </div>
        </div>
      )}
    </div>
  );
}

function SnapBlock({ label, value, hint, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-medium">{label}</div>
      <div className={`font-heading text-[26px] font-semibold leading-none mt-2.5 tabular-nums tracking-tight ${accent || "text-white"}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
    </div>
  );
}

function ActionMenu({ tenant, plans, onAction, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // "plan" | "grant" | null
  const [newPlan, setNewPlan] = useState(tenant.plan);
  const [busy, setBusy] = useState(false);
  // Estado do modal "Liberar acesso"
  const [grantPlan, setGrantPlan] = useState("base");
  const [grantDays, setGrantDays] = useState(30);
  const [grantReason, setGrantReason] = useState("");
  // Estado do modal "Conceder créditos (cortesia)"
  // Diferente de "Liberar acesso": NÃO mexe no plano nem no status,
  // só credita N créditos avulsos na carteira. Útil pra
  // compensações, mimo de relacionamento, ou bônus de feedback.
  const [giftAmount, setGiftAmount] = useState(1000);
  const [giftReason, setGiftReason] = useState("");

  const run = async (kind, extra = {}) => {
    setBusy(true);
    try {
      const payload = modal === "plan" ? { plan: newPlan } : extra;
      await onAction(kind, payload);
      setModal(null);
      setOpen(false);
    } finally { setBusy(false); }
  };

  const submitGrant = async () => {
    if (grantReason.trim().length < 5) return;
    await run("grant", {
      plan_code: grantPlan, period_days: grantDays, reason: grantReason.trim(),
    });
    setGrantReason("");
  };

  const submitCreditGift = async () => {
    if (giftReason.trim().length < 5) return;
    if (giftAmount <= 0 || giftAmount > 1_000_000) return;
    await run("credit-gift", {
      amount: Math.floor(Number(giftAmount)),
      reason: giftReason.trim(),
    });
    setGiftReason("");
    setGiftAmount(1000);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="tenant-actions-btn"
        className="inline-flex items-center gap-2 bg-white/10 border border-white/10 text-white px-3 py-1.5 rounded-md text-[13px] hover:bg-white/20 transition-colors"
      >
        Ações <DotsThreeVertical size={14} weight="regular" />
      </button>
      {open && (
        <div className="absolute top-10 right-0 w-56 bg-slate-900 border border-white/10 rounded-md shadow-2xl z-40 py-1">
          <MenuItem icon={SignIn}   label="Impersonate"       onClick={() => { setModal(null); run("impersonate"); }} />
          <MenuItem icon={Rocket}   label="Preflight Check"   onClick={() => { setOpen(false); setModal("preflight"); }} />
          <MenuItem icon={Sparkle}  label="Alterar plano"     onClick={() => setModal("plan")} />
          {tenant.status === "active" ? (
            <MenuItem icon={Pause}  label="Suspender"         onClick={() => { setModal(null); run("suspend"); }} />
          ) : (
            <MenuItem icon={Play}   label="Reativar"          onClick={() => { setModal(null); run("activate"); }} />
          )}
          <div className="border-t border-white/10 my-1" />
          {tenant.is_manual_grant ? (
            <MenuItem icon={ArrowUUpLeft} label="Revogar cortesia"  onClick={() => { setModal(null); run("revoke-grant"); }} />
          ) : (
            <MenuItem icon={Gift}     label="Liberar acesso (cortesia)" onClick={() => setModal("grant")} />
          )}
          <MenuItem icon={Coins}    label="Conceder créditos (cortesia)" onClick={() => setModal("credit-gift")} />
          {tenant.is_internal ? (
            <MenuItem icon={Buildings} label="Remover marcação 'interno'"
                      onClick={() => { setModal(null); run("toggle-internal", { is_internal: false }); }} />
          ) : (
            <MenuItem icon={Buildings} label="Marcar como tenant interno"
                      onClick={() => { setModal(null); run("toggle-internal", { is_internal: true }); }} />
          )}
          <MenuItem icon={XCircle}  label="Cancelar cliente"  onClick={() => { setModal(null); run("cancel"); }} danger />
          <div className="border-t border-white/10 my-1" />
          <MenuItem icon={Lock}     label="Resetar senha"     onClick={() => { setModal(null); run("reset-password"); }} />
          <div className="border-t border-white/10 my-1" />
          <MenuItem icon={Trash}    label="Excluir do banco (irreversível)" onClick={() => { setOpen(false); setModal("hard-delete"); }} danger />
        </div>
      )}

      {modal === "hard-delete" && (
        <HardDeleteModal
          tenantId={tenant.id}
          tenantName={tenant.name}
          onClose={() => setModal(null)}
          onDeleted={() => { setModal(null); onDeleted && onDeleted(); }}
        />
      )}

      {modal === "preflight" && (
        <PreflightModal
          tenantId={tenant.id}
          tenantName={tenant.name}
          onClose={() => setModal(null)}
        />
      )}

      {modal === "plan" && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-white/10 rounded-lg p-6 w-full max-w-sm" data-testid="change-plan-modal">
            <h3 className="font-heading text-base font-semibold text-white">Alterar plano</h3>
            <p className="text-[12px] text-slate-400 mt-1">Mudanças de plano recalculam MRR imediatamente.</p>
            <select
              value={newPlan}
              onChange={(e) => setNewPlan(e.target.value)}
              data-testid="change-plan-select"
              className="mt-4 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            >
              {(() => {
                // Dedupe by `code` (production seeds occasionally double-insert
                // plans; until the dedupe migration runs, the UI must not show
                // 2x "Trial — R$ 0"). Also hide non-billable special plans
                // (`trial`, `enterprise`, anything with `active=false`) from
                // the "alterar plano" action — those are assigned via
                // dedicated flows (manual_grant) not free-form selection.
                const seen = new Set();
                const visible = (plans || []).filter((p) => {
                  if (seen.has(p.code)) return false;
                  seen.add(p.code);
                  if (p.active === false) return false;
                  if (p.code === "trial" || p.code === "enterprise") return false;
                  return true;
                });
                if (visible.length === 0) {
                  return <option disabled>Nenhum plano comercial ativo</option>;
                }
                return visible.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name} — {fmtBRL(p.price_cents)}
                  </option>
                ));
              })()}
            </select>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="text-[12px] text-slate-400 hover:text-white">Cancelar</button>
              <button onClick={() => run("change-plan")} disabled={busy} data-testid="change-plan-confirm"
                      className="bg-white text-slate-900 px-3.5 py-1.5 rounded-md text-[12px] font-semibold hover:bg-slate-200 disabled:opacity-60">
                {busy ? "Salvando…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "grant" && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-white/10 rounded-lg p-6 w-full max-w-md" data-testid="grant-modal">
            <div className="flex items-center gap-2">
              <Gift size={18} weight="duotone" className="text-amber-300" />
              <h3 className="font-heading text-base font-semibold text-white">Liberar acesso (cortesia)</h3>
            </div>
            <p className="text-[12px] text-slate-400 mt-2 leading-relaxed">
              Equivale a um pagamento Stripe — o tenant fica <strong className="text-white">active</strong>,
              recebe os créditos do plano e opera normal. Marcado como <code className="text-amber-200">manual_grant</code> e
              excluído de relatórios financeiros (ARR/MRR).
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Plano</label>
                <select
                  value={grantPlan}
                  onChange={(e) => setGrantPlan(e.target.value)}
                  data-testid="grant-plan-select"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
                >
                  <option value="essential">Essencial — R$ 299 / 5.000 créditos</option>
                  <option value="base">Enterprise — R$ 799 / 18.000 créditos</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Validade (dias)</label>
                <select
                  value={grantDays}
                  onChange={(e) => setGrantDays(Number(e.target.value))}
                  data-testid="grant-days-select"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
                >
                  <option value={7}>7 dias (curto)</option>
                  <option value={30}>30 dias (padrão)</option>
                  <option value={60}>60 dias</option>
                  <option value={90}>90 dias (trimestre)</option>
                  <option value={180}>180 dias (semestre)</option>
                  <option value={365}>365 dias (ano)</option>
                </select>
                <p className="text-[10.5px] text-slate-500 mt-1">
                  Após o vencimento o tenant cai para <code>past_due</code> — você pode renovar abrindo o menu de novo.
                </p>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Motivo (auditado)</label>
                <textarea
                  rows={3}
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  placeholder="Ex: Parceiro estratégico — Marcelo Omega super troca, validando o produto antes da contratação"
                  data-testid="grant-reason"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 resize-none"
                />
                <p className="text-[10.5px] text-slate-500 mt-1">
                  {grantReason.length < 5
                    ? `Mínimo 5 caracteres (${grantReason.length}/5)`
                    : `${grantReason.length} caracteres`}
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="text-[12px] text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={submitGrant}
                disabled={busy || grantReason.trim().length < 5}
                data-testid="grant-confirm"
                className="bg-amber-300 text-slate-900 px-3.5 py-1.5 rounded-md text-[12px] font-semibold hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? "Liberando…" : "Liberar acesso"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "credit-gift" && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-white/10 rounded-lg p-6 w-full max-w-md" data-testid="credit-gift-modal">
            <div className="flex items-center gap-2">
              <Coins size={18} weight="duotone" className="text-emerald-300" />
              <h3 className="font-heading text-base font-semibold text-white">Conceder créditos (cortesia)</h3>
            </div>
            <p className="text-[12px] text-slate-400 mt-2 leading-relaxed">
              Adiciona créditos avulsos à carteira do tenant. <strong className="text-white">NÃO altera o plano nem o status</strong> — útil pra compensação, bonificação ou mimo de relacionamento.
              Cada transação fica registrada no extrato como <code className="text-emerald-200">admin_gift</code>.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Quantidade de créditos</label>
                <input
                  type="number"
                  min={1}
                  max={1_000_000}
                  step={100}
                  value={giftAmount}
                  onChange={(e) => setGiftAmount(Number(e.target.value) || 0)}
                  data-testid="credit-gift-amount"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white tabular-nums focus:outline-none focus:border-white/30"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[500, 1000, 5000, 15000, 50000].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setGiftAmount(v)}
                      data-testid={`credit-gift-preset-${v}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                        giftAmount === v
                          ? "bg-emerald-400/15 border-emerald-400/40 text-emerald-200"
                          : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      +{v.toLocaleString("pt-BR")}
                    </button>
                  ))}
                </div>
                <p className="text-[10.5px] text-slate-500 mt-1">
                  Mín 1 · Máx 1.000.000 créditos por operação. Para valores maiores, faça em múltiplas operações.
                </p>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Motivo (auditado)</label>
                <textarea
                  rows={3}
                  value={giftReason}
                  onChange={(e) => setGiftReason(e.target.value)}
                  placeholder="Ex: Compensação pelo incidente do webhook em 18/06 · ou: Bonificação por feedback detalhado da nova feature"
                  data-testid="credit-gift-reason"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 resize-none"
                />
                <p className="text-[10.5px] text-slate-500 mt-1">
                  {giftReason.length < 5
                    ? `Mínimo 5 caracteres (${giftReason.length}/5)`
                    : `${giftReason.length} caracteres`}
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="text-[12px] text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={submitCreditGift}
                disabled={
                  busy ||
                  giftReason.trim().length < 5 ||
                  !giftAmount ||
                  giftAmount <= 0 ||
                  giftAmount > 1_000_000
                }
                data-testid="credit-gift-confirm"
                className="bg-emerald-400 text-slate-900 px-3.5 py-1.5 rounded-md text-[12px] font-semibold hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? "Creditando…" : `Creditar ${(giftAmount || 0).toLocaleString("pt-BR")} 🎁`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={`tenant-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`w-full text-left text-[13px] px-3 py-2 flex items-center gap-2.5 hover:bg-white/5 transition-colors ${
        danger ? "text-rose-400 hover:text-rose-300" : "text-slate-200"
      }`}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}

function TabUso({ data }) {
  if (!data) return <Loading />;
  const t = data.totals;
  return (
    <div className="space-y-5" data-testid="tab-uso">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="Enviadas" icon={ChatCircleDots}>
          <div className="font-heading text-2xl font-semibold text-white tabular-nums">{t.messages_sent.toLocaleString("pt-BR")}</div>
        </Card>
        <Card title="Recebidas" icon={ChatCircleDots}>
          <div className="font-heading text-2xl font-semibold text-white tabular-nums">{t.messages_received.toLocaleString("pt-BR")}</div>
        </Card>
        <Card title="Handoffs humanos" icon={UsersThree}>
          <div className="font-heading text-2xl font-semibold text-amber-400 tabular-nums">{t.handoffs_requested}</div>
        </Card>
        <Card title="Leads capturados" icon={UsersThree}>
          <div className="font-heading text-2xl font-semibold text-emerald-400 tabular-nums">{t.leads_captured}</div>
        </Card>
      </div>

      <Card title={`Mensagens — últimos ${data.since_days} dias`} icon={ChartBar}>
        {data.series.length === 0 ? (
          <div className="text-[12px] text-slate-500 py-4">Sem atividade no período.</div>
        ) : (
          <div className="flex items-end gap-1 h-24 mt-2">
            {data.series.map((s, i) => {
              const max = Math.max(1, ...data.series.map((x) => x.sent + x.received));
              const h = ((s.sent + s.received) / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${s.date}: ${s.sent + s.received} msgs`}>
                  <div className="w-full bg-white/10 rounded-sm transition-all" style={{ height: `${Math.max(2, h)}%` }} />
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Totais" icon={Buildings}>
        <StatRow label="Clientes cadastrados" value={t.customers_total} />
        <StatRow label="Agendamentos no período" value={t.appointments_in_period} />
        <StatRow label="Agendamentos (total)" value={t.appointments_total} />
      </Card>
    </div>
  );
}

function _providerLabel(integration) {
  const p = (integration?.provider || integration?.kind || "").toLowerCase();
  if (p.includes("z_api") || p.includes("zapi") || p.includes("z-api")) return "Z-API";
  if (p.includes("whatsapp_cloud") || p.includes("cloud")) return "WhatsApp Cloud API";
  if (p.includes("twilio")) return "Twilio";
  if (p.includes("wati")) return "WATI";
  if (p.includes("gupshup")) return "Gupshup";
  return integration?.provider || integration?.kind || "—";
}

function TabWhatsApp({ data }) {
  if (!data) return <Loading />;
  const status = data.integration?.status;
  return (
    <div className="space-y-5" data-testid="tab-whatsapp">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card title="Provider" icon={WhatsappLogo}>
          <div className="font-heading text-lg font-semibold text-white">
            {data.integration ? _providerLabel(data.integration) : "—"}
          </div>
          <div className={`text-[12px] mt-1 ${status === "connected" ? "text-emerald-400" : "text-amber-400"}`}>
            {status || "não conectado"}
          </div>
        </Card>
        <Card title="Mensagens enviadas" icon={ChatCircleDots}>
          <div className="font-heading text-2xl font-semibold text-white tabular-nums">{data.stats.messages_sent}</div>
        </Card>
        <Card title="Falhas" icon={XCircle}>
          <div className={`font-heading text-2xl font-semibold tabular-nums ${data.stats.messages_failed > 0 ? "text-rose-400" : "text-white"}`}>{data.stats.messages_failed}</div>
        </Card>
      </div>

      {data.integration && (
        <Card title="Configuração (mascarada)" icon={Lock}>
          <pre className="text-[11px] text-slate-300 bg-black/30 rounded-md p-3 overflow-auto">{JSON.stringify(data.integration.config, null, 2)}</pre>
        </Card>
      )}

      <Card title="Falhas recentes" icon={ClockCounterClockwise}>
        {data.recent_failures.length === 0 ? (
          <div className="text-[12px] text-slate-500 py-2">Nenhuma falha recente. 🎉</div>
        ) : data.recent_failures.map((f) => (
          <StatRow key={f.id} label={new Date(f.created_at).toLocaleString("pt-BR")} value={(f.detail?.error || "erro").slice(0, 60)} accent="text-rose-400" />
        ))}
      </Card>
    </div>
  );
}

function TabFinanceiro({ data }) {
  if (!data) return <Loading />;
  const limits = data.plan_doc?.limits || {};
  return (
    <div className="space-y-5" data-testid="tab-financeiro">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="MRR" icon={CurrencyDollar}>
          <div className="font-heading text-2xl font-semibold text-white tabular-nums">{fmtBRLn(data.mrr_brl)}</div>
          <div className="text-[11px] text-slate-500 mt-1">plano atual: {data.tenant_plan}</div>
        </Card>
        <Card title="Custo IA" icon={Sparkle}>
          <div className={`font-heading text-2xl font-semibold tabular-nums ${data.ai_cost_brl > 50 ? "text-rose-400" : "text-white"}`}>{fmtBRLn(data.ai_cost_brl)}</div>
        </Card>
        <Card title="Margem" icon={ChartBar}>
          <div className={`font-heading text-2xl font-semibold tabular-nums ${data.estimated_margin_brl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtBRLn(data.estimated_margin_brl)}</div>
        </Card>
        <Card title="Receita gerada" icon={Receipt}>
          <div className="font-heading text-2xl font-semibold text-emerald-400 tabular-nums">{fmtBRL(data.ai_attributed_revenue_cents)}</div>
          <div className="text-[11px] text-slate-500 mt-1">atribuída à IA</div>
        </Card>
      </div>

      {data.plan_doc && (
        <Card title={`Limites — plano ${data.plan_doc.name}`} icon={Lock}>
          <StatRow label="Clientes ativos" value={limits.max_active_customers || "Ilimitado"} />
          <StatRow label="Mensagens/mês" value={limits.monthly_messages || "Ilimitado"} />
          <StatRow label="Chamadas LLM premium/mês" value={limits.monthly_premium_llm_calls || "Ilimitado"} />
          <StatRow label="Help Assistant/dia" value={limits.daily_help_assistant || "Ilimitado"} />
          <div className="pt-3 mt-2 border-t border-white/5">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-2">Recursos</div>
            <div className="flex flex-wrap gap-1.5">
              {(data.plan_doc.features || []).map((f) => (
                <span key={f} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/10 text-slate-300">{f}</span>
              ))}
            </div>
          </div>
        </Card>
      )}

      <WalletHistory
        balance={data.credits_balance ?? 0}
        transactions={data.credit_transactions || []}
      />
    </div>
  );
}

// Last 10 wallet movements + current balance. Useful when a customer
// asks "you said you gave me X credits last week, where are they?" —
// you find the answer in 2 seconds without leaving the page.
function WalletHistory({ balance, transactions }) {
  return (
    <Card title="Carteira de créditos" icon={Coins}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500">Saldo atual</div>
          <div className="font-heading text-2xl font-semibold text-emerald-300 tabular-nums">
            {Number(balance).toLocaleString("pt-BR")}
          </div>
        </div>
        <div className="text-[11px] text-slate-500">
          {transactions.length === 0 ? "sem movimentos" : `últimos ${transactions.length} movimentos`}
        </div>
      </div>

      {transactions.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic">Sem movimentos registrados nesta carteira.</div>
      ) : (
        <div className="divide-y divide-white/5 -mx-1">
          {transactions.map((tx) => {
            const isCredit = tx.amount > 0;
            const sourceLabel = _walletSourceLabel(tx.source);
            const sourceTone = _walletSourceTone(tx.source);
            const reason =
              tx.metadata?.reason ||
              tx.metadata?.pack_code ||
              tx.metadata?.action_code ||
              null;
            return (
              <div
                key={tx.id}
                className="flex items-start justify-between gap-3 py-2.5 px-1"
                data-testid={`wallet-tx-${tx.id}`}
                title={JSON.stringify(tx.metadata || {})}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded ${sourceTone}`}>
                      {sourceLabel}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {new Date(tx.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  {reason && (
                    <div className="text-[12px] text-slate-300 mt-1 truncate">{reason}</div>
                  )}
                  {tx.actor && (
                    <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">
                      por {tx.actor.replace(/^super_admin:/, "").replace(/^agent$/, "agente (IA)")}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-heading text-sm font-semibold tabular-nums ${isCredit ? "text-emerald-400" : "text-rose-400"}`}>
                    {isCredit ? "+" : ""}{Number(tx.amount).toLocaleString("pt-BR")}
                  </div>
                  <div className="text-[10px] text-slate-500 tabular-nums">
                    saldo {Number(tx.balance_after).toLocaleString("pt-BR")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function _walletSourceLabel(source) {
  const s = String(source || "");
  if (s === "admin_gift")      return "Cortesia (admin)";
  if (s === "manual_grant")    return "Liberação manual";
  if (s === "plan_renewal")    return "Renovação do plano";
  if (s === "initial_setup")   return "Setup inicial";
  if (s.startsWith("purchase:")) return `Pacote · ${s.slice(9)}`;
  if (s.startsWith("promo:"))    return `Promo · ${s.slice(6)}`;
  if (s.startsWith("debit:"))    return `Uso · ${s.slice(6)}`;
  return s || "—";
}

function _walletSourceTone(source) {
  const s = String(source || "");
  if (s === "admin_gift")        return "bg-emerald-400/10 text-emerald-200 border border-emerald-400/20";
  if (s === "manual_grant")      return "bg-amber-300/10 text-amber-200 border border-amber-300/20";
  if (s.startsWith("purchase:")) return "bg-sky-400/10 text-sky-200 border border-sky-400/20";
  if (s === "plan_renewal")      return "bg-violet-400/10 text-violet-200 border border-violet-400/20";
  if (s.startsWith("debit:"))    return "bg-rose-400/10 text-rose-200 border border-rose-400/20";
  return "bg-white/5 text-slate-300 border border-white/10";
}

/* ─────────── Audit trail (events, by user, by AI, by system) ─────────── */
function TabAudit({ tenantId, tenantName }) {
  const [logs, setLogs] = useState(null);
  const [prefix, setPrefix] = useState("");
  const [days, setDays]   = useState(30);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams({ limit: 200, days });
    if (prefix) params.set("action_prefix", prefix);
    api.get(`/admin/tenants/${tenantId}/logs?${params}`).then(({ data }) => setLogs(data));
  }, [tenantId, prefix, days]);

  // CSV export — the operator opens the page, optionally filters by
  // prefix + period, then downloads. The CSV uses ; as separator to
  // open cleanly in Brazilian Excel installs (which default to ;).
  const exportCsv = async () => {
    if (!logs || logs.length === 0) return;
    setExporting(true);
    try {
      const rows = [
        ["Data/Hora", "Ação", "Ator", "Tipo do alvo", "ID do alvo", "Status", "Detalhe"],
        ...logs.map((l) => [
          new Date(l.created_at).toLocaleString("pt-BR"),
          l.action || "",
          l.actor || "",
          l.target_type || "",
          l.target_id || "",
          l.status || "",
          JSON.stringify(l.detail || {}).replace(/\n/g, " ").replace(/;/g, ","),
        ]),
      ];
      const csv = rows
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
        .join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }); // BOM for Excel
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      a.href = URL.createObjectURL(blob);
      a.download = `auditoria_${(tenantName || tenantId).toLowerCase().replace(/\s+/g, "_")}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setExporting(false);
    }
  };

  // Pre-built filter chips: most operators want one of these 5 cuts.
  // Keeps the UI minimal for the 80% case while the free-text input
  // remains available for advanced prefixes (decision., webhook., …).
  const QUICK_FILTERS = [
    { label: "Tudo",         value: "" },
    { label: "Mensagens",    value: "message." },
    { label: "Agendamentos", value: "appointment." },
    { label: "Credenciais",  value: "integration." },
    { label: "Login/Logout", value: "auth." },
    { label: "Decisões IA",  value: "decision." },
  ];

  return (
    <div className="space-y-4" data-testid="tab-audit">
      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-md p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`audit-days-${d}`}
              className={`px-2.5 py-1 text-[11px] rounded transition-colors ${
                days === d ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {QUICK_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setPrefix(f.value)}
              data-testid={`audit-quickfilter-${f.label}`}
              className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                prefix === f.value
                  ? "bg-white/15 text-white"
                  : "bg-white/[0.03] text-slate-400 hover:text-white hover:bg-white/[0.06]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="Prefixo personalizado…"
          data-testid="audit-prefix-filter"
          className="flex-1 min-w-[180px] bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-[12px] text-white placeholder:text-slate-600 focus:outline-none focus:border-white/30"
        />

        <button
          onClick={exportCsv}
          disabled={!logs?.length || exporting}
          data-testid="audit-export-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3.5 py-1.5 text-[12px] font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <DownloadSimple size={12} weight="bold" />
          {exporting ? "Gerando…" : "Exportar CSV"}
        </button>
      </div>

      {/* Summary line */}
      {logs && (
        <div className="text-[11.5px] text-slate-500">
          Mostrando {logs.length} evento{logs.length === 1 ? "" : "s"} dos últimos {days} dias
          {prefix && <> · filtro <code className="text-slate-300">{prefix}</code></>}
        </div>
      )}

      {/* Logs list */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
        {logs === null && <div className="p-8 text-center"><CircleNotch className="animate-spin inline text-slate-500" /></div>}
        {logs?.length === 0 && (
          <div className="p-10 text-center text-[12.5px] text-slate-500">
            Nenhum evento de auditoria nesse recorte.
          </div>
        )}
        {logs?.map((l) => (
          <div
            key={l.id}
            data-testid={`audit-row-${l.id}`}
            className="px-4 py-3 border-b border-white/5 last:border-b-0 flex items-start gap-3 hover:bg-white/[0.02]"
          >
            <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center shrink-0 mt-0.5">
              {l.actor === "agent"
                ? <Robot size={12} weight="fill" className="text-blue-400" />
                : l.actor === "customer"
                  ? <UserIcon size={12} weight="fill" className="text-emerald-400" />
                  : l.actor?.startsWith("user:")
                    ? <UserIcon size={12} weight="fill" className="text-slate-300" />
                    : <Gear size={12} weight="fill" className="text-slate-500" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12.5px] text-white font-mono truncate">{l.action}</span>
                <span className="text-[11px] text-slate-500">por {l.actor || "?"}</span>
                {l.target_type && (
                  <span className="text-[11px] text-slate-500">
                    · {l.target_type}:{l.target_id?.slice(0, 8)}
                  </span>
                )}
                {l.status === "error" && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-semibold">erro</span>
                )}
              </div>
              {l.detail && Object.keys(l.detail).length > 0 && (
                <pre className="mt-1 text-[10.5px] text-slate-500 font-mono bg-white/[0.02] border border-white/5 rounded px-2 py-1 overflow-x-auto">
                  {JSON.stringify(l.detail, null, 0).slice(0, 280)}
                </pre>
              )}
            </div>
            <div className="text-[10.5px] text-slate-500 tabular-nums shrink-0">
              {new Date(l.created_at).toLocaleString("pt-BR")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Loading() {
  return <div className="p-8 text-center"><CircleNotch className="animate-spin inline text-slate-500" size={20} /></div>;
}

export default function AdminTenantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("geral");
  // v0.99.10 — Sandbox de Setor (só admin master)
  const [sectorModalOpen, setSectorModalOpen] = useState(false);
  const [sectorCatalog, setSectorCatalog] = useState([]);
  const [newSector, setNewSector] = useState("");
  const [sectorBusy, setSectorBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const [detail, pl] = await Promise.all([
      api.get(`/admin/tenants/${id}?days=30`),
      api.get("/admin/plans"),
    ]);
    setData(detail.data);
    setPlans(pl.data);
  };

  useEffect(() => {
    load();
    api.post(`/admin/tenants/${id}/log-access`).catch(() => {});
  }, [id]);

  // v0.99.10 — carrega catálogo de setores ao abrir o modal (uma vez)
  useEffect(() => {
    if (sectorModalOpen && sectorCatalog.length === 0) {
      api.get("/admin/sector-catalog")
        .then(({ data }) => setSectorCatalog(data?.sectors || []))
        .catch(() => toast.error("Falha ao carregar setores"));
    }
    if (sectorModalOpen && data?.tenant?.sector) {
      setNewSector(data.tenant.sector);
    }
  }, [sectorModalOpen, sectorCatalog.length, data?.tenant?.sector]);

  const submitSectorChange = async () => {
    if (!newSector) return toast.error("Escolha um setor");
    if (newSector === data?.tenant?.sector) {
      setSectorModalOpen(false);
      return;
    }
    setSectorBusy(true);
    try {
      const { data: res } = await api.post(
        `/admin/tenants/${id}/set-sector`,
        { sector: newSector },
      );
      toast.success(`Setor alterado: ${res.from} → ${res.to}`);
      setSectorModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alterar setor");
    } finally {
      setSectorBusy(false);
    }
  };

  useEffect(() => {
    if (tab === "uso" && !usage)
      api.get(`/admin/tenants/${id}/usage?days=30`).then(({ data }) => setUsage(data));
    if (tab === "whatsapp" && !whatsapp)
      api.get(`/admin/tenants/${id}/whatsapp?days=7`).then(({ data }) => setWhatsapp(data));
    if (tab === "financeiro" && !financial)
      api.get(`/admin/tenants/${id}/financial`).then(({ data }) => setFinancial(data));
  }, [tab, id, usage, whatsapp, financial]);

  const onAction = async (kind, payload = {}) => {
    const t = data.tenant;
    try {
      if (kind === "suspend")    await api.patch(`/admin/tenants/${id}`, { status: "suspended" });
      if (kind === "activate")   await api.patch(`/admin/tenants/${id}`, { status: "active" });
      if (kind === "cancel") {
        if (!window.confirm(`Cancelar definitivamente ${t.name}? Essa ação é auditada.`)) return;
        await api.patch(`/admin/tenants/${id}`, { status: "cancelled" });
      }
      if (kind === "change-plan") await api.patch(`/admin/tenants/${id}`, { plan: payload.plan });
      if (kind === "reset-password") {
        const { data: r } = await api.post(`/admin/tenants/${id}/reset-password`);
        window.prompt(
          `Senha temporária gerada para ${r.user_email}. Copie agora, não será exibida novamente:`,
          r.temporary_password,
        );
      }
      if (kind === "impersonate") {
        const { data: imp } = await api.post(`/admin/tenants/${id}/impersonate`);
        startImpersonate({
          token:  imp.token,
          user:   { id: "impersonated", email: imp.user_email, name: imp.user_name,
                     role: "owner", tenant_id: imp.tenant_id },
          tenant: { id: imp.tenant_id, name: imp.tenant_name },
          byEmail: imp.impersonated_by,
        });
        toast.success(`Impersonando ${imp.tenant_name}`);
        window.location.href = "/app";
        return;
      }
      if (kind === "grant") {
        // Liberação manual (sem Stripe) — cortesia / parceiro / validação.
        // O backend exige `reason` (mín 5 chars). `period_days` default 30.
        const { data: g } = await api.post(`/admin/grants`, {
          tenant_id: id,
          plan_code: payload.plan_code || "base",
          period_days: Number(payload.period_days || 30),
          reason: payload.reason,
        });
        toast.success(
          `Cortesia ativada — ${g.credits_granted.toLocaleString("pt-BR")} créditos · expira em ${new Date(g.period_end).toLocaleDateString("pt-BR")}`,
        );
      }
      if (kind === "revoke-grant") {
        const reason = window.prompt("Motivo da revogação (será auditado):", "");
        if (!reason || reason.trim().length < 5) {
          toast.error("Motivo precisa ter pelo menos 5 caracteres.");
          return;
        }
        await api.post(`/admin/grants/${id}/revoke`, { reason });
        toast.success("Cortesia revogada — tenant voltou para past_due.");
      }
      if (kind === "credit-gift") {
        // Crédito avulso (sem mexer no plano/status). Backend exige
        // amount 1..1.000.000 e reason mín 5 chars.
        const { data: g } = await api.post(
          `/admin/tenants/${id}/credits/gift`,
          { amount: payload.amount, reason: payload.reason },
        );
        toast.success(
          `+${g.amount_granted.toLocaleString("pt-BR")} créditos · saldo agora ${g.new_balance.toLocaleString("pt-BR")}`,
        );
      }
      if (kind === "toggle-internal") {
        await api.post(`/admin/tenants/${id}/internal`, {
          is_internal: !!payload?.is_internal,
        });
        toast.success(
          payload?.is_internal
            ? "Marcado como tenant interno · não conta no financeiro."
            : "Marcação 'interno' removida.",
        );
      }
      toast.success("Ação aplicada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  if (!data) return <Loading />;
  const t = data.tenant;

  const fixPlan = async () => {
    // Corrige plano gravado errado (ex.: pagou Essencial e ficou "base").
    const target = t.plan_code === "essential" ? "base" : "essential";
    const label = target === "essential" ? "Essencial · R$ 299/mês" : "Enterprise · R$ 799/mês";
    const reason = window.prompt(
      `Corrigir o plano de "${t.name}" para ${label}?\n\n` +
      "Os créditos do período serão reajustados pela DIFERENÇA entre os planos " +
      "(nunca fica negativo).\n\nMotivo (obrigatório, mín. 5 caracteres):",
    );
    if (reason === null) return;
    if (!reason || reason.trim().length < 5) {
      toast.error("Motivo precisa ter pelo menos 5 caracteres.");
      return;
    }
    try {
      const { data: r } = await api.post(`/admin/tenants/${id}/fix-plan`, {
        plan_code: target, adjust_credits: true, reason: reason.trim(),
      });
      const delta = r.credit_delta_applied || 0;
      toast.success(
        `Plano corrigido: ${r.old_plan_code || "—"} → ${r.plan_code} · créditos ` +
        `${delta >= 0 ? "+" : ""}${delta.toLocaleString("pt-BR")} · saldo ` +
        `${(r.credits_balance || 0).toLocaleString("pt-BR")}`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao corrigir plano");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto">
      <button onClick={() => navigate("/admin-master/tenants")} data-testid="tenant-detail-back"
              className="flex items-center gap-1.5 text-[12px] text-slate-400 hover:text-white transition-colors mb-5">
        <ArrowLeft size={12} weight="regular" /> Voltar para tenants
      </button>

      <header className="flex items-start justify-between mb-7 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Tenant</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">{t.name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-[12px] text-slate-400 capitalize">{t.sector || "—"}</span>
            <span className="text-slate-600">·</span>
            <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/10 text-slate-300">{t.plan || "trial"}</span>
            <span className="text-slate-600">·</span>
            <span className={`text-[11px] px-2 py-0.5 rounded ${
              t.status === "active" ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20"
                : t.status === "suspended" ? "bg-rose-400/10 text-rose-400 border border-rose-400/20"
                : t.status === "cancelled" ? "bg-slate-500/10 text-slate-500 border border-slate-500/20"
                : "bg-amber-400/10 text-amber-400 border border-amber-400/20"
            }`}>{t.status || "active"}</span>
            {t.is_manual_grant && (
              <>
                <span className="text-slate-600">·</span>
                <span
                  data-testid="grant-badge"
                  title={`Cortesia ativa — concedida por ${t.manual_grant_granted_by || "?"} · expira em ${t.current_period_end ? new Date(t.current_period_end).toLocaleDateString("pt-BR") : "?"}\n\nMotivo: ${t.manual_grant_reason || "—"}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-amber-300/10 text-amber-300 border border-amber-300/30"
                >
                  <Gift size={11} weight="fill" /> Cortesia
                </span>
              </>
            )}
            {t.is_internal && (
              <>
                <span className="text-slate-600">·</span>
                <span
                  data-testid="internal-badge"
                  title="Tenant interno — excluído de listagens e financeiro do Admin Master."
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-sky-400/10 text-sky-300 border border-sky-400/30"
                >
                  <Buildings size={11} weight="fill" /> Interno
                </span>
              </>
            )}
          </div>
        </div>
        <ActionMenu
          tenant={t}
          plans={plans}
          onAction={onAction}
          onDeleted={() => navigate("/admin-master/tenants")}
        />
      </header>

      <div className="border-b border-white/5 flex gap-6 mb-6 overflow-x-auto">
        {TABS.map((x) => (
          <button
            key={x.id}
            onClick={() => setTab(x.id)}
            data-testid={`tenant-tab-${x.id}`}
            className={`pb-3 text-[13px] font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === x.id ? "text-white border-white" : "text-slate-500 hover:text-slate-300 border-transparent"
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {tab === "geral" && (
        <div className="space-y-5" data-testid="tab-geral">
          {data.health && <HealthDetailCard health={data.health} />}

          {/* ─── SAÚDE FINANCEIRA — bloco principal do gestor ──── */}
          {data.financial_snapshot && (
            <FinancialSnapshotCard
              snap={data.financial_snapshot}
              tokensTotal={data.ai.tokens_total_estimated}
              aiCalls={data.ai.calls}
              periodDays={data.financial_snapshot.period_days}
            />
          )}

          {/* ─── 4 cards consolidados ─────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Empresa" icon={Buildings}>
              <StatRow label="Nome" value={t.name} />
              <StatRow label="Setor" value={t.sector || "—"} />
              {/* v0.99.10 — Sandbox de Setor. Só o Master pode trocar
                  o segmento de um tenant (para QA ou correção). Muda
                  persona/UI, NÃO apaga dados. */}
              <div className="flex justify-end -mt-1 mb-2">
                <button
                  data-testid="change-sector-btn"
                  onClick={() => setSectorModalOpen(true)}
                  className="text-[11px] text-slate-500 hover:text-sky-400 transition-colors"
                >
                  ⇄ alterar setor
                </button>
              </div>
              {/* v0.96.17 — Label do plano é dinâmica pelo `plan_doc` que
                  vem do backend (source of truth). Cai em fallback só se
                  o plan_doc não tiver sido carregado. Assim uma mudança
                  de preço/nome no /admin/plans reflete imediatamente
                  sem precisar deploy. */}
              <StatRow label="Plano"
                value={data.plan_doc
                  ? `${data.plan_doc.name} · ${fmtBRL(data.plan_doc.price_cents)}/mês`
                  : (t.plan_code === "essential" ? "Essencial · R$ 299/mês"
                    : t.plan_code === "base" ? "Enterprise · R$ 799/mês"
                    : t.plan_code ? t.plan_code : "Enterprise (legado)")}
                accent="text-emerald-400" />
              <div className="flex justify-end -mt-1 mb-1">
                <button
                  data-testid="fix-plan-btn"
                  onClick={fixPlan}
                  className="text-[11px] text-slate-500 hover:text-emerald-400 transition-colors"
                >
                  ⇄ corrigir plano
                </button>
              </div>
              <StatRow label="Status assinatura" value={t.status || "active"}
                accent={t.status === "active" ? "text-emerald-400"
                  : t.status === "suspended" ? "text-rose-400"
                  : t.status === "cancelled" ? "text-slate-500" : "text-amber-400"} />
              <StatRow label="Criado em" value={t.created_at ? new Date(t.created_at).toLocaleDateString("pt-BR") : "—"} />
              <StatRow label="Último acesso" value={data.last_activity ? new Date(data.last_activity).toLocaleString("pt-BR") : "—"}
                accent="text-slate-300" />
              <StatRow label="WhatsApp"
                value={data.whatsapp_status === "connected" ? "✓ conectado"
                  : data.whatsapp_status === "configured" ? "configurado"
                  : "desconectado"}
                accent={data.whatsapp_status === "connected" ? "text-emerald-400"
                  : data.whatsapp_status === "configured" ? "text-amber-400"
                  : "text-slate-500"} />
            </Card>

            <Card title="Volume operacional" icon={UsersThree}>
              <StatRow label="Usuários da equipe" value={data.general.users_count} />
              <StatRow label="Clientes cadastrados" value={data.general.customers_count} />
              <StatRow label="Veículos cadastrados" value={data.general.assets_count} />
              <StatRow label="Mensagens enviadas" value={data.usage.messages_sent.toLocaleString("pt-BR")} />
              <StatRow label="Mensagens recebidas" value={data.usage.messages_received.toLocaleString("pt-BR")} />
              <StatRow label="Agendamentos gerados" value={data.usage.appointments} accent="text-emerald-400" />
              <StatRow label="Leads capturados" value={data.usage.leads_captured} accent="text-emerald-400" />
              <StatRow label="Handoffs humanos" value={data.usage.handoffs}
                accent={(data.usage.handoffs || 0) > 5 ? "text-amber-400" : "text-slate-300"} />
              <StatRow label="Falhas no período" value={data.usage.failures}
                accent={(data.usage.failures || 0) > 0 ? "text-rose-400" : "text-slate-300"} />
            </Card>

            <Card title="Consumo de IA" icon={Sparkle}>
              <StatRow label="Chamadas Sonnet" value={data.ai.calls.orchestrator} accent="text-blue-400" />
              <StatRow label="Vision (CRLV)" value={data.ai.calls.vision} accent="text-blue-400" />
              <StatRow label="Help Assistant" value={data.ai.calls.help} accent="text-blue-400" />
              <StatRow label="Fast-path (R$ 0)" value={data.ai.calls.fast_path} accent="text-emerald-400" />
              <StatRow label="Decisões via template (R$ 0)" value={data.ai.calls.decisions} accent="text-emerald-400" />
              <StatRow label="Tokens (input)" value={(data.ai.tokens_in_estimated || 0).toLocaleString("pt-BR")} />
              <StatRow label="Tokens (output)" value={(data.ai.tokens_out_estimated || 0).toLocaleString("pt-BR")} />
              <StatRow label="Fast-path ratio" value={`${(data.ai.fast_path_ratio * 100).toFixed(1)}%`} accent="text-emerald-400" />
            </Card>

            <Card title="Equipe do cliente" icon={UsersThree}>
              {(data.general.users || []).length === 0 && <div className="text-[12px] text-slate-500">Sem usuários.</div>}
              {(data.general.users || []).map((user) => (
                <div key={user.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
                  <div>
                    <div className="text-sm text-white">{user.name}</div>
                    <div className="text-[11px] text-slate-500">{user.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/10 text-slate-400">{user.role}</span>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {tab === "uso"        && <TabUso data={usage} />}
      {tab === "whatsapp"   && <TabWhatsApp data={whatsapp} />}
      {tab === "financeiro" && <TabFinanceiro data={financial} />}
      {tab === "auditoria"  && <TabAudit tenantId={id} tenantName={data?.tenant?.name} />}

      {tab === "ia" && (
        <div className="space-y-5" data-testid="tab-ia">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card title="Custo IA estimado" icon={CurrencyDollar}>
              <div className="font-heading text-2xl font-semibold text-white tabular-nums">{fmtBRLn(data.ai.cost_total_brl)}</div>
              <div className="text-[11px] text-slate-500 mt-1">últimos 30 dias</div>
            </Card>
            <Card title="LLM dependency" icon={ChartBar}>
              <div className="font-heading text-2xl font-semibold text-white tabular-nums">{(data.ai.llm_dependency * 100).toFixed(1)}%</div>
              <div className="text-[11px] text-slate-500 mt-1">% que cai no Sonnet</div>
            </Card>
            <Card title="Fast path ratio" icon={Lightning}>
              <div className="font-heading text-2xl font-semibold text-emerald-400 tabular-nums">{(data.ai.fast_path_ratio * 100).toFixed(1)}%</div>
              <div className="text-[11px] text-slate-500 mt-1">economia determinística</div>
            </Card>
            <Card title="Total de chamadas" icon={Sparkle}>
              <div className="font-heading text-2xl font-semibold text-white tabular-nums">{Object.values(data.ai.calls).reduce((a, b) => a + b, 0)}</div>
            </Card>
          </div>
          <Card title="Distribuição por motor" icon={Sparkle}>
            <StatRow label="Sonnet (orchestrator)" value={data.ai.calls.orchestrator} accent="text-blue-400" />
            <StatRow label="Vision (CRLV)" value={data.ai.calls.vision} accent="text-blue-400" />
            <StatRow label="Help Assistant" value={data.ai.calls.help} accent="text-blue-400" />
            <StatRow label="Intent fast-path (R$ 0)" value={data.ai.calls.fast_path} accent="text-emerald-400" />
            <StatRow label="Decisões via template (R$ 0)" value={data.ai.calls.decisions} accent="text-emerald-400" />
          </Card>
          <Card title="Custo por motor" icon={CurrencyDollar}>
            <StatRow label="Orchestrator (Sonnet)" value={fmtBRLn(data.ai.cost_breakdown_brl.orchestrator)} />
            <StatRow label="Vision" value={fmtBRLn(data.ai.cost_breakdown_brl.vision)} />
            <StatRow label="Help Assistant" value={fmtBRLn(data.ai.cost_breakdown_brl.help)} />
            <StatRow label="Ecossistema de Documentos" value={fmtBRLn(data.ai.cost_breakdown_brl.documents)} />
            {Number(data.ai.cost_breakdown_brl.other) > 0 && (
              <StatRow label="Outros motores" value={fmtBRLn(data.ai.cost_breakdown_brl.other)} />
            )}
            <div className="pt-3 mt-2 border-t border-white/10 flex items-center justify-between">
              <span className="text-[12px] text-slate-300 font-medium">Total</span>
              <span className="text-base text-white font-semibold tabular-nums">{fmtBRLn(data.ai.cost_total_brl)}</span>
            </div>
          </Card>
        </div>
      )}

      {/* v0.99.10 — Sandbox de Setor modal */}
      {sectorModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !sectorBusy && setSectorModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-white/10 rounded-lg p-6 w-full max-w-md" data-testid="change-sector-modal">
            <h3 className="font-heading text-base font-semibold text-white">Alterar setor do tenant</h3>
            <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">
              Muda a persona da IA e a UI para o setor escolhido. <span className="text-amber-300">Não apaga dados existentes</span> — cadastros que não fizerem sentido no novo setor continuam salvos.
            </p>
            <div className="mt-4">
              <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1.5">Setor atual</label>
              <div className="text-[13px] text-slate-300 mb-3 capitalize">{data?.tenant?.sector || "(vazio)"}</div>
              <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1.5">Novo setor</label>
              <select
                data-testid="change-sector-select"
                value={newSector}
                onChange={(e) => setNewSector(e.target.value)}
                disabled={sectorBusy || sectorCatalog.length === 0}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
              >
                {sectorCatalog.length === 0 ? (
                  <option>Carregando...</option>
                ) : (
                  sectorCatalog.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.display_name} ({s.code})
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setSectorModalOpen(false)}
                disabled={sectorBusy}
                className="text-[12px] text-slate-400 hover:text-white disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={submitSectorChange}
                disabled={sectorBusy || !newSector || newSector === data?.tenant?.sector}
                data-testid="change-sector-confirm"
                className="bg-sky-500 text-white px-3.5 py-1.5 rounded-md text-[12px] font-semibold hover:bg-sky-600 disabled:opacity-60"
              >
                {sectorBusy ? "Alterando…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Preflight Modal — só super_admin enxerga, acessível via menu Ações.
// ──────────────────────────────────────────────────────────────────────
//
// Pedido Marcelo Omega jun/2026: o operador final NÃO deve ver "vou
// passar vergonha?" — todo cliente parte do princípio que funciona.
// Aqui o super admin escolhe o tenant no painel, abre Ações → Preflight
// Check, digita um telefone (próprio ou de teste) e recebe um veredicto
// PT-BR com 4 detalhes: modo de recepção, Z-API, campanhas na fila e
// handoff. Backend: GET /api/admin/tenants/{tid}/preflight?phone=X.

const PREFLIGHT_VERDICT = {
  READY:   { bg: "bg-emerald-500/10",  border: "border-emerald-500/30", text: "text-emerald-300", icon: "🚀", label: "Pronto" },
  WARN:    { bg: "bg-amber-500/10",    border: "border-amber-500/30",   text: "text-amber-300",   icon: "⚠️", label: "Atenção" },
  DANGER:  { bg: "bg-rose-500/10",     border: "border-rose-500/30",    text: "text-rose-300",    icon: "🔴", label: "Perigo" },
  BLOCKED: { bg: "bg-slate-500/10",    border: "border-slate-400/30",   text: "text-slate-300",   icon: "❌", label: "Bloqueado" },
};

function PreflightModal({ tenantId, tenantName, onClose }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const params = phone.trim() ? `?phone=${encodeURIComponent(phone.trim())}` : "";
      const { data } = await api.get(`/admin/tenants/${tenantId}/preflight${params}`);
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao rodar diagnóstico");
    } finally {
      setBusy(false);
    }
  };

  const v = report?.verdict ? (PREFLIGHT_VERDICT[report.verdict] || PREFLIGHT_VERDICT.WARN) : null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-white/10 rounded-lg p-6 w-full max-w-2xl max-h-[88vh] overflow-y-auto"
        data-testid="admin-preflight-modal"
      >
        <header className="flex items-start gap-3 mb-4">
          <span className="shrink-0 w-9 h-9 rounded-lg bg-white/10 text-white flex items-center justify-center">
            <Rocket size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base font-semibold text-white">Preflight Check</h3>
            <p className="text-[12px] text-slate-400 mt-0.5 truncate">
              {tenantName} — diagnóstico ao vivo antes de testar do celular
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white -mt-1 -mr-1">
            <XCircle size={20} weight="regular" />
          </button>
        </header>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
            placeholder="Telefone com DDI+DDD (ex: 5561991690110)"
            data-testid="admin-preflight-phone"
            className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            maxLength={15}
          />
          <button
            type="button"
            onClick={run}
            disabled={busy}
            data-testid="admin-preflight-run"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-white text-slate-900 text-[13px] font-semibold hover:bg-slate-200 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? (
              <><ArrowsClockwise size={13} className="animate-spin" /> Checando…</>
            ) : (
              <><Rocket size={13} weight="fill" /> Verificar</>
            )}
          </button>
        </div>

        {report && (
          <div className="space-y-3" data-testid="admin-preflight-report">
            <div
              className={`rounded-lg border ${v.border} ${v.bg} px-4 py-3`}
              data-testid={`admin-preflight-verdict-${report.verdict}`}
            >
              <div className={`text-[10.5px] uppercase tracking-[0.14em] font-semibold ${v.text}`}>
                {v.icon} {v.label}
              </div>
              <div className={`text-[13.5px] mt-1 leading-snug ${v.text}`}>
                {report.verdict_human}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <AdminDetailRow
                label="Modo de recepção"
                value={
                  report.mode === "test_safe"
                    ? `Teste (${report.whitelist_count} na whitelist)`
                    : report.mode === "production_lead_capture"
                    ? "Produção (IA responde todo mundo)"
                    : report.mode || "—"
                }
                ok={report.mode === "production_lead_capture" || (report.mode === "test_safe" && report.whitelist_count > 0)}
              />
              <AdminDetailRow
                label="WhatsApp (Z-API)"
                value={
                  report.integration
                    ? `${report.integration.provider} · ${report.integration.status}${report.integration.connected_phone ? ` · ${report.integration.connected_phone}` : ""}`
                    : "Não conectado"
                }
                ok={!!report.integration?.connected_phone_captured}
              />
              <AdminDetailRow
                label="Campanhas pendentes"
                value={`${report.campaigns?.scheduled_count ?? 0} agendada(s) · ${report.campaigns?.running_count ?? 0} rodando · ${report.campaigns?.safety_blocked_count ?? 0} bloqueada(s)`}
                ok={(report.campaigns?.will_fire_on_next_tick || 0) === 0}
                danger={(report.campaigns?.will_fire_on_next_tick || 0) > 0}
              />
              <AdminDetailRow
                label="Handoff pendente"
                value={`${report.handoff?.open_pending_questions ?? 0} pergunta(s) na fila`}
                ok={(report.handoff?.open_pending_questions || 0) === 0}
              />
              {report.sim && (
                <div className="md:col-span-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                    Simulação do telefone
                  </div>
                  <div className="text-[12.5px] text-white mt-1 font-mono">
                    {report.sim.input_raw} → {report.sim.input_canonical}
                  </div>
                  <div className="text-[12px] text-slate-300 mt-1">{report.sim.decision}</div>
                </div>
              )}
            </div>

            {(report.campaigns?.details_scheduled || []).length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2.5">
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-300 font-semibold mb-1.5">
                  Campanhas na fila ({report.campaigns.details_scheduled.length}):
                </div>
                <ul className="text-[12px] text-amber-100 space-y-0.5">
                  {report.campaigns.details_scheduled.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2">
                      <span className="truncate">{c.name}</span>
                      <span className="font-mono text-amber-300 whitespace-nowrap">
                        {c.audience_size} contato(s) · {c.scheduled_at?.slice(0, 16)?.replace("T", " ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminDetailRow({ label, value, ok, danger }) {
  const color = danger ? "text-rose-300" : ok ? "text-emerald-300" : "text-slate-300";
  const dot = danger ? "bg-rose-400" : ok ? "bg-emerald-400" : "bg-slate-500";
  return (
    <div className="rounded-md border border-white/10 px-3 py-2.5 bg-white/[0.02] flex items-start gap-2">
      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">{label}</div>
        <div className={`text-[12px] ${color} mt-0.5 break-words`}>{value}</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Hard Delete Modal — apagar tenant do banco (irreversível)
// ──────────────────────────────────────────────────────────────────────
//
// Pedido Marcelo jun/2026: precisava limpar Omegas duplicadas e tenants
// de teste sem precisar de mongo shell. Como é destrutivo, exigimos
// digitação do nome do tenant ANTES de habilitar o botão. Mostramos
// também o count exato do que foi removido pra dar transparência.

function HardDeleteModal({ tenantId, tenantName, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = confirmText.trim().toLowerCase() === (tenantName || "").trim().toLowerCase();

  const submit = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/tenants/${tenantId}`, {
        data: { confirm_name: confirmText.trim() },
      });
      toast.success(
        `${tenantName} apagado · ${data.total_docs_deleted} documento(s) removido(s)`,
        { duration: 6000 }
      );
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-rose-500/30 rounded-lg p-6 w-full max-w-md"
        data-testid="hard-delete-modal"
      >
        <header className="flex items-start gap-3 mb-4">
          <span className="shrink-0 w-10 h-10 rounded-lg bg-rose-500/20 text-rose-300 flex items-center justify-center">
            <Warning size={20} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base font-semibold text-white">Excluir cliente do banco</h3>
            <p className="text-[12.5px] text-rose-300/90 mt-0.5 leading-snug">
              Ação <strong>irreversível</strong>. Apaga o tenant, usuários, clientes, conversas,
              mensagens, agendamentos, campanhas e tudo associado. Audit log permanece.
            </p>
          </div>
        </header>

        <div className="rounded-md border border-rose-500/30 bg-rose-500/[0.06] px-3 py-2.5 mb-3">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-rose-300 font-semibold">Cliente</div>
          <div className="text-[14px] text-white font-medium mt-0.5 break-words">{tenantName}</div>
          <div className="text-[11px] text-slate-400 font-mono mt-1">{tenantId}</div>
        </div>

        <label className="block text-[12.5px] text-slate-300 mb-1.5">
          Pra confirmar, digite o nome do cliente abaixo:
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={tenantName}
          data-testid="hard-delete-confirm-input"
          autoComplete="off"
          className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/50 font-mono"
        />

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="hard-delete-cancel"
            className="text-[12.5px] text-slate-300 px-3 py-1.5 rounded-md hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!matches || busy}
            data-testid="hard-delete-confirm"
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-[12.5px] font-semibold ${
              matches
                ? "bg-rose-500 text-white hover:bg-rose-600"
                : "bg-slate-700 text-slate-500 cursor-not-allowed"
            }`}
          >
            {busy ? (
              <><ArrowsClockwise size={13} className="animate-spin" /> Apagando…</>
            ) : (
              <><Trash size={13} weight="fill" /> Excluir permanentemente</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
